import React, { useState, useRef, useEffect, useCallback } from 'react'
import RightArrow from '../../common/icons/right-arrow'
import { useTranslation } from 'react-i18next'
import { _Api } from '../../api/_call'
import './barcode.scss'
import TableLoader from '../../common/TableLoader'
import Select from 'react-select'
import { Pagination } from '../../common/Pagination'
import SearchIcon from '../../common/icons/SearchIcon'
import CloseIcon from '../../common/icons/CloseIcon'
import GreenTick from '../../common/icons/GreenTick'
import { DateRange } from '../../../components/common/helpers/daterange'
import PrintIcon from '../../../Ecommerce/template_two/default/assets/icons/PrintIcon'
import Calendar from '../../common/icons/sidebar/Calendar'

const BarcodeSummary = () => {
  const [activeTab, setActiveTab] = useState("stockid");
  const [expandedRows, setExpandedRows] = useState([]);
  const { t } = useTranslation();
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [activeSubtask, setActiveSubtask] = useState('Collapse all');
  const subtaskRef = useRef(null);
  const [state, setstate] = useState({
    totalPages: 0,
    currentPage: 1,
  });
  const [showloader, setShowloader] = useState(false);
  const { totalPages, currentPage } = state;
  const [showSearch, setShowSearch] = useState(false);
  const [filtername, setFiltername] = useState('');
  const [limit, setLimit] = useState(100);
  const [datefilter, setDatefilter] = useState([]);
  const [logdata, setLogdata] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [selectedDetailIds, setSelectedDetailIds] = useState([]);

  const groupBy = activeTab === "sku" ? "sku" : "stock_id";

  const getlogdata = useCallback(async (current = 1, firstlimit, overrides = {}) => {
    const silent = Boolean(overrides.silent);
    if (!silent) setShowloader(true);
    try {
      const pageLimit = firstlimit ? firstlimit : limit;
      const skipNumber = current ? (current - 1) * pageLimit : 0;
      const searchValue = overrides.search !== undefined ? overrides.search : filtername;
      const dates = overrides.datefilter !== undefined ? overrides.datefilter : datefilter;
      const tabGroup = overrides.group_by || groupBy;

      const postdata = {
        group_by: tabGroup,
        search: searchValue || "",
        limit: pageLimit,
        skip: skipNumber,
      };

      if (dates?.[0]) {
        postdata.start_date = dates[0];
      }
      if (dates?.[1]) {
        postdata.end_date = dates[1];
      }

      const res = await _Api(postdata, `api/v1/barcode/print-log/summary`);
      if (res?.code === 200) {
        const rows = Array.isArray(res?.data) ? res.data : [];
        setLogdata(rows);
        setstate({
          totalPages: Math.ceil((res?.total || 0) / pageLimit) || 0,
          currentPage: current ? current : 1,
        });

        if (activeSubtask === 'Expand all') {
          setExpandedRows(rows.map((row) => String(row.id)));
        } else if (overrides.resetExpand) {
          setExpandedRows([]);
        }
      } else {
        setLogdata([]);
        setstate({ totalPages: 0, currentPage: 1 });
        setExpandedRows([]);
      }
    } catch (err) {
      console.log(err, "err");
      setLogdata([]);
      setExpandedRows([]);
    } finally {
      if (!silent) setShowloader(false);
    }
  }, [filtername, limit, datefilter, groupBy, activeSubtask]);

  useEffect(() => {
    if (datefilter?.length) {
      getlogdata(1, limit, { resetExpand: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, datefilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subtaskRef.current && !subtaskRef.current.contains(event.target)) {
        setShowSubtasks(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePaginations = (current) => {
    setstate((prev) => ({ ...prev, currentPage: current }));
    getlogdata(current);
  };

  const getlimit = (nextLimit) => {
    setLimit(nextLimit);
    getlogdata(1, nextLimit, { resetExpand: true });
  };

  const filterbyname = () => {
    getlogdata(1, limit, { resetExpand: true });
  };

  const clearSearch = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const hadSearch = Boolean(filtername?.trim());
    setFiltername("");
    setShowSearch(false);
    if (hadSearch) {
      getlogdata(1, limit, { search: "", resetExpand: true, silent: true });
    }
  };

  const toggleSearchBar = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (showSearch) {
      setFiltername("");
      setShowSearch(false);
      return;
    }
    setShowSearch(true);
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setExpandedRows([]);
    setSelectedParentIds([]);
    setSelectedDetailIds([]);
    setstate({ totalPages: 0, currentPage: 1 });
  };

  const toggleRow = (id) => {
    const rowId = String(id);
    setExpandedRows((prev) =>
      prev.includes(rowId) ? prev.filter((item) => item !== rowId) : [...prev, rowId]
    );
  };

  const applySubtaskMode = (mode) => {
    setActiveSubtask(mode);
    setShowSubtasks(false);
    if (mode === 'Expand all') {
      setExpandedRows(logdata.map((row) => String(row.id)));
    } else {
      setExpandedRows([]);
    }
  };

  const toggleParentSelection = (id) => {
    const rowId = String(id);
    setSelectedParentIds((prev) =>
      prev.includes(rowId) ? prev.filter((item) => item !== rowId) : [...prev, rowId]
    );
  };

  const toggleDetailSelection = (id) => {
    const rowId = String(id);
    setSelectedDetailIds((prev) =>
      prev.includes(rowId) ? prev.filter((item) => item !== rowId) : [...prev, rowId]
    );
  };

  const selectOptions = [
    { label: 10, value: 10 },
    { label: 100, value: 100 },
    { label: 200, value: 200 },
    { label: 300, value: 300 }
  ];

  const showStockId = activeTab === "stockid";
  const colSpan = showStockId ? 10 : 9;

  const allParentIds = logdata.map((row) => String(row.id));
  const allParentsSelected =
    allParentIds.length > 0 && allParentIds.every((id) => selectedParentIds.includes(id));

  const toggleSelectAllParents = () => {
    if (allParentsSelected) {
      setSelectedParentIds([]);
    } else {
      setSelectedParentIds(allParentIds);
    }
  };

  const renderCheckbox = (id, checked, onChange) => (
    <div className="com-check summary-check">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
      />
      <label htmlFor={id} className="com-check-label" />
    </div>
  );

  const renderInnerColgroup = () => (
    <colgroup>
      <col className="summary-col-expand" />
      <col className="summary-col-check" />
      <col className="summary-col-index" />
      {showStockId ? <col className="summary-col-stockid" /> : null}
      <col className="summary-col-sku" />
      <col className="summary-col-user" />
      <col className="summary-col-template" />
      <col className="summary-col-printout" />
      <col className="summary-col-datetime" />
      <col className="summary-col-action" />
    </colgroup>
  );

  const renderDetailRows = (details = []) => {
    if (!details.length) {
      return (
        <tr>
          <td colSpan={colSpan} className="text-center">{t("No Data Available In Table")}</td>
        </tr>
      );
    }

    return details.map((detail, detailKey) => {
      const detailId = String(detail?.id || detailKey);
      return (
        <tr className="hoverrow text-nowrap" key={detailId}>
          <td></td>
          <td style={{ padding:'12px 10px 12px 4px' }}>
            {renderCheckbox(
              `detail-check-${detailId}`,
              selectedDetailIds.includes(detailId),
              () => toggleDetailSelection(detailId)
            )}
          </td>
          <td></td>
          {/* User under StockID, Template under SKU (matches design) */}
          {showStockId ? <td><span>{detail?.user || "-"}</span></td> : null}
          <td><span>{showStockId ? (detail?.template || "-") : (detail?.user || "-")}</span></td>
          <td><span>{showStockId ? "" : (detail?.template || "-")}</span></td>
          <td></td>
          <td><span>{detail?.printout ?? 1}</span></td>
          <td><span>{detail?.date_time || "-"}</span></td>
          <td style={{textAlign:'center'}}>
            <span className="summary-print-btn"><PrintIcon color="#A4A4A4" /></span>
          </td>
        </tr>
      );
    });
  };

  const renderExpandedTable = (result) => (
    <tr className="summary-expand-row">
      <td colSpan={colSpan} className="p-0">
        <div className="summary-inner-wrap">
          <table className="summary-inner-table">
            {renderInnerColgroup()}
            <thead>
              <tr className="text-nowrap">
                <th></th>
                <th></th>
                <th></th>
                {/* User under StockID, Template under SKU, gap under User+Template */}
                {showStockId ? <th>{t("User")}</th> : null}
                <th>{showStockId ? t("Template") : t("User")}</th>
                <th>{showStockId ? "" : t("Template")}</th>
                <th></th>
                <th>{t("Print out")}</th>
                <th>{t("Date & Time")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {renderDetailRows(result?.details)}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <div className="main-content-wrapper-body summarypage setup-sticky-header">
        <div className="main-content-wrapper-body-top-bar d-flex align-items-center justify-content-between">
          <div className="main-content-wrapper-body-top-bar-left">
            <h1 className="main-content-wrapper-body-top-bar-left-heading fw-semibold mb-0">
              {t('Setup')} <RightArrow /> {t('Barcode')} <RightArrow />
              <span className="">{t('Summary')}</span>
            </h1>
          </div>
        </div>
        <div className="main-body-top-tab-bar">
          <div className="main-body-top-tab-bar-left-col nav nav-pills" role="tablist">
            <div
              className={
                activeTab === 'stockid'
                  ? 'main-body-top-tab-bar-left-col-a nav-link active cursor-pointer'
                  : 'main-body-top-tab-bar-left-col-a nav-link cursor-pointer'
              }
              onClick={() => handleTabChange("stockid")}
            >
              <span className="main-body-top-tab-bar-left-col-a-text">{t("Stock ID")}</span>
            </div>
            <div
              className={
                activeTab === 'sku'
                  ? 'main-body-top-tab-bar-left-col-a nav-link active cursor-pointer'
                  : 'main-body-top-tab-bar-left-col-a nav-link cursor-pointer'
              }
              onClick={() => handleTabChange("sku")}
            >
              <span className="main-body-top-tab-bar-left-col-a-text">{t("SKU")}</span>
            </div>
          </div>
        </div>
        <div className="main-content-wrapper-body-top-bar-right summarypage-subheader">
          <div className="summarypage-subheader-left">
            <Select
              className="limit-select"
              options={selectOptions}
              value={{ label: limit, value: limit }}
              onChange={(e) => { setLimit(e.value); getlimit(e.value) }}
            />
            <div className="summarypage-subheader-left-filters">
              <span className="summarypage-subheader-left-filters-title">{t("Smart Filters")}:</span>
              <div className="position-relative" ref={subtaskRef}>
                <div
                  className={`calendarlist-headbar-left-subtasks ${activeSubtask === 'Expand all' ? 'active' : ''}`}
                  onClick={() => setShowSubtasks(!showSubtasks)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 13C17.1046 13 18 12.1046 18 11C18 9.89543 17.1046 9 16 9C14.8954 9 14 9.89543 14 11C14 12.1046 14.8954 13 16 13Z" stroke={activeSubtask === 'Expand all' ? '#52CBC4' : "#A0A0A0"} />
                    <path d="M16 21C17.1046 21 18 20.1046 18 19C18 17.8954 17.1046 17 16 17C14.8954 17 14 17.8954 14 19C14 20.1046 14.8954 21 16 21Z" stroke={activeSubtask === 'Expand all' ? '#52CBC4' : "#A0A0A0"} />
                    <path d="M5 3C3.89543 3 3 3.89543 3 5C3 6.10457 3.89543 7 5 7C6.10457 7 7 6.10457 7 5C7 3.89543 6.10457 3 5 3Z" stroke={activeSubtask === 'Expand all' ? '#52CBC4' : "#A0A0A0"} />
                    <path d="M5 8V15C5 16.8856 5 17.8284 5.58579 18.4142C6.17157 19 7.11438 19 9 19H14" stroke={activeSubtask === 'Expand all' ? '#52CBC4' : "#A0A0A0"} />
                    <path d="M5 7C5 8.88562 5 9.82843 5.58579 10.4142C6.17157 11 7.11438 11 9 11H14" stroke={activeSubtask === 'Expand all' ? '#52CBC4' : "#A0A0A0"} />
                  </svg>
                  <span>{t("Subtasks")}</span>
                </div>
                {showSubtasks ? (
                  <div className="calendarlist-headbar-left-subtaskdropdown">
                    <div className="calendarlist-headbar-left-subtaskdropdown-inner">
                      <div className="calendarlist-headbar-left-subtaskdropdown-inner-title">{t("Show Subtasks")}</div>
                      <div className="calendarlist-headbar-left-subtaskdropdown-inner-list">
                        <div
                          className="calendarlist-headbar-left-subtaskdropdown-inner-list-item"
                          onClick={() => applySubtaskMode('Collapse all')}
                        >
                          <div className="heading">
                            {t("Collapse all")}
                            <span className="default">{t("(default)")}</span>
                          </div>
                          {activeSubtask === 'Collapse all' ? <div><GreenTick /></div> : ""}
                        </div>
                        <div
                          className="calendarlist-headbar-left-subtaskdropdown-inner-list-item"
                          onClick={() => applySubtaskMode('Expand all')}
                        >
                          <div>{t("Expand all")}</div>
                          {activeSubtask === 'Expand all' ? <div><GreenTick /></div> : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="summarypage-subheader-right">
            <div
              className={`com-search-bar2 barcode-search-bar position-relative ${showSearch ? "active" : ""}`}
            >
              <span
                className="com-search-bar2-searchbar"
                onClick={toggleSearchBar}
              >
                <SearchIcon />
              </span>
              <input
                placeholder={t("Search by user, SKU, stock ID, template")}
                className="h-100 border-0 bg-transparent"
                value={filtername}
                onKeyDown={(e) => e.key === 'Enter' && filterbyname()}
                onChange={(e) => setFiltername(e.target.value)}
              />
              <span
                className="com-search-bar2-closeicon"
                onClick={clearSearch}
                role="button"
                tabIndex={showSearch ? 0 : -1}
                aria-hidden={!showSearch}
                onKeyDown={(e) => e.key === 'Enter' && clearSearch(e)}
              >
                <CloseIcon />
              </span>
            </div>
            <div className="summarypage-subheader-right-calendarholder">
              <span className="summarypage-subheader-right-calendarholder-icon">
                <Calendar color="#A4A4A4" width="20px" height="20px" />
              </span>
              <DateRange setDatefilter={setDatefilter} />
            </div>
            <span className="summarypage-subheader-right-printer"><PrintIcon color="#A4A4A4" /></span>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-md-12">
            <div className="main-body-main-table-wrap com-custom-table table-responsive position-relative">
              <table id="barcode_summary" className="com-custom-table-tag table align-middle summary-table">
                {renderInnerColgroup()}
                <thead>
                  <tr className="bg-white text-nowrap align-middle">
                    <th className="summary-col-expand bg-white"></th>
                    <th className="summary-col-check bg-white">
                      {renderCheckbox(
                        "summary-select-all",
                        allParentsSelected,
                        toggleSelectAllParents
                      )}
                    </th>
                    <th className="summary-col-index bg-white">#</th>
                    {showStockId ? (
                      <th className="summary-col-stockid bg-white">{t("StockID")}</th>
                    ) : null}
                    <th className="summary-col-sku bg-white">{t("SKU")}</th>
                    <th className="summary-col-user bg-white">{t("User")}</th>
                    <th className="summary-col-template bg-white">{t("Template")}</th>
                    <th className="summary-col-printout bg-white">{t("Print out")}</th>
                    <th className="summary-col-datetime bg-white">{t("Date & Time")}</th>
                    <th className="summary-col-action bg-white" style={{ paddingRight:'12px' }}>{t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {logdata.length > 0 ? logdata.map((result, key) => {
                    const rowId = String(result?.id);
                    const isExpanded = expandedRows.includes(rowId);
                    const rowNumber = ((currentPage - 1) * limit) + key + 1;
                    return (
                      <React.Fragment key={rowId}>
                        <tr className={`hoverrow text-nowrap summary-parent-row ${isExpanded ? "is-expanded" : ""}`}>
                          <td className="summary-col-expand">
                            <span
                              className={`summary-expand-icon cursor-pointer ${isExpanded ? "open" : ""}`}
                              onClick={() => toggleRow(rowId)}
                            >
                              <RightArrow color="#52CBC4" />
                            </span>
                          </td>
                          <td className="summary-col-check">
                            {renderCheckbox(
                              `parent-check-${rowId}`,
                              selectedParentIds.includes(rowId),
                              () => toggleParentSelection(rowId)
                            )}
                          </td>
                          <td className="summary-col-index"><span>{rowNumber}</span></td>
                          {showStockId ? (
                            <td className="summary-col-stockid">
                              <span className="link-color">{result?.stockid || "-"}</span>
                            </td>
                          ) : null}
                          <td className="summary-col-sku">
                            <span className="link-color">{result?.SKU || "-"}</span>
                          </td>
                          <td className="summary-col-user"><span>{result?.user ?? "-"}</span></td>
                          <td className="summary-col-template"><span>{result?.template ?? "-"}</span></td>
                          <td className="summary-col-printout"><span>{result?.printout ?? "-"}</span></td>
                          <td className="summary-col-datetime"><span>{result?.date_time || "-"}</span></td>
                          <td className="summary-col-action">
                            <span className="summary-print-btn"><PrintIcon color="#A4A4A4" /></span>
                          </td>
                        </tr>
                        {isExpanded ? renderExpandedTable(result) : null}
                      </React.Fragment>
                    );
                  }) : !showloader && (
                    <tr>
                      <td colSpan={colSpan} className="text-center">
                        {t("No Data Available In Table")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {showloader && <TableLoader />}
            </div>
            {logdata.length > 0 && (
              <Pagination
                total={totalPages}
                current={currentPage}
                pagination={(crPage) => handlePaginations(crPage)}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default BarcodeSummary
